import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Snowflake, Clock, ShoppingBag, Plane } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  freezeActive: boolean
  freezeAvailable: boolean
  hoursRemaining?: number | null
  globalFreezeAvailable?: boolean
}

/**
 * Объясняет клиенту, как работают заморозки. Открывается тапом по ❄️-чипу
 * на главной (где показан баланс).
 */
export function FreezeInfoSheet({
  open, onClose, freezeActive, freezeAvailable, hoursRemaining, globalFreezeAvailable = true,
}: Props) {
  const dragControls = useDragControls()
  const navigate = useNavigate()
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  const stateLabel = freezeActive
    ? `Активна · ещё ${hoursRemaining != null ? Math.ceil(hoursRemaining) : '—'} ч`
    : freezeAvailable
      ? 'Готова к активации'
      : 'Использована'
  const stateColor = freezeActive ? '#FF8A00' : freezeAvailable ? '#1F8FE5' : '#9AA5B4'

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 410,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="freeze-info-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y" dragListener={false} dragControls={dragControls}
            dragConstraints={{ top: 0 }} dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '92vh',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab', touchAction: 'none' }}
            >
              <SheetGrip onClose={onClose} />
            </div>

            <div style={{ position: 'relative', padding: '8px 20px 6px' }}>
              <button
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 4, right: 16,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>

              {/* Hero — текущее состояние */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingRight: 40, paddingTop: 4 }}>
                <div style={{
                  width: 62, height: 62, borderRadius: 18,
                  background: freezeActive
                    ? 'linear-gradient(135deg,#FF9500,#FF6B00)'
                    : freezeAvailable
                      ? 'linear-gradient(135deg,#4A9FE5,#6C5CE7)'
                      : 'rgba(160,174,192,0.20)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: freezeAvailable || freezeActive
                    ? '0 8px 22px rgba(74,159,229,0.35), 0 0 0 1.5px rgba(255,255,255,0.6) inset'
                    : 'none',
                }}>
                  <Snowflake size={30} color={freezeAvailable || freezeActive ? 'white' : '#9AA5B4'} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA5B4', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Заморозка
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: stateColor, marginTop: 2 }}>
                    {stateLabel}
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7B8D', marginTop: 4 }}>
                    Баланс: <b style={{ color: '#1C2D41' }}>{freezeAvailable ? 1 : 0}</b> авто-заморозка{freezeActive ? ' · сейчас работает' : ''}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '6px 16px 0', minHeight: 0 }}>
              {/* Что это */}
              <div style={{
                background: 'rgba(74,159,229,0.06)',
                border: '1px solid rgba(74,159,229,0.18)',
                borderRadius: 14, padding: 12,
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', marginBottom: 4 }}>
                  Как работает заморозка
                </div>
                <p style={{ fontSize: 12, color: '#3D4A5C', margin: 0, lineHeight: 1.55 }}>
                  Если ты не отметил активность <b>34 часа</b>, автоматически включается заморозка ❄️ — она даёт ещё <b>24 часа</b> на возврат, не списывая жизнь.
                </p>
                <p style={{ fontSize: 12, color: '#3D4A5C', margin: '6px 0 0', lineHeight: 1.55 }}>
                  Стартовая заморозка одна на программу. Дополнительные — в Магазине (10 TuCoin = +24 ч защиты).
                </p>
              </div>

              {/* Глобальная (отпуск/болезнь) */}
              <div style={{
                background: 'rgba(108,92,231,0.06)',
                border: '1px solid rgba(108,92,231,0.18)',
                borderRadius: 14, padding: 12,
                marginBottom: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 9, background: 'rgba(108,92,231,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Plane size={14} color="#6C5CE7" />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41' }}>
                    Глобальная заморозка — для отпуска / болезни
                  </div>
                </div>
                <p style={{ fontSize: 12, color: '#3D4A5C', margin: 0, lineHeight: 1.55 }}>
                  Замораживает всю программу на 7 дней. Куратор не тревожит, жизни и режим не теряются. <b>Бесплатно, 1 раз на программу.</b>
                </p>
              </div>

              {/* Таймлайн */}
              <div style={{
                background: 'white',
                border: '1px solid rgba(26,58,92,0.06)',
                borderRadius: 14, padding: 12,
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#1C2D41', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Clock size={13} color="#4A9FE5" /> Как сработает
                </div>
                {[
                  { hours: '0–34 ч', text: 'Всё хорошо. Никаких санкций.', color: '#34C759' },
                  { hours: '34 ч простоя', text: 'Если заморозка есть — включается на 24 ч. Если нет — сразу −1 жизнь.', color: '#1F8FE5' },
                  // #309: −1 жизнь НЕ сбивает стаж (#290) — обнуление только при 0 жизней.
                  { hours: '58 ч (34+24)', text: 'Если за это время не вернулся — −1 жизнь. Стаж дней сохраняется.', color: '#FF9500' },
                  { hours: 'Каждые +24 ч', text: 'Дальше каждые 24 ч простоя — ещё −1 жизнь. Стаж обнулится, только когда сгорят все жизни.', color: '#FF3B30' },
                ].map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid rgba(26,58,92,0.06)' }}>
                    <span style={{
                      flexShrink: 0, width: 8, height: 8, borderRadius: '50%',
                      background: r.color, marginTop: 5,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: r.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        {r.hours}
                      </div>
                      <div style={{ fontSize: 12, color: '#1C2D41', marginTop: 2, lineHeight: 1.4 }}>
                        {r.text}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA в магазин */}
              {!freezeActive && (
                <button
                  onClick={() => { onClose(); navigate('/shop') }}
                  style={{
                    width: '100%', height: 48, borderRadius: 14, border: 'none',
                    background: 'linear-gradient(135deg, #4A9FE5 0%, #6C5CE7 100%)',
                    color: 'white', fontSize: 14, fontWeight: 800,
                    cursor: 'pointer', fontFamily: F,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: '0 6px 18px rgba(74,159,229,0.35)',
                    marginBottom: 12,
                  }}
                >
                  <ShoppingBag size={16} /> {freezeAvailable ? 'Купить ещё в магазине' : 'Купить в магазине · 10 TuCoin'}
                </button>
              )}
              {!globalFreezeAvailable && (
                <div style={{ fontSize: 11, color: '#9AA5B4', textAlign: 'center', marginBottom: 8 }}>
                  Глобальная заморозка уже использована в этой программе
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
